import {
  Authority,
  Meta as UserMeta,
  checkAuthority,
} from "providers/A12n/index"
import {
  BoViewType,
  BookingOrder,
  BookingOrderStatus,
} from "models/bookingOrder"

const INTENTIONS = [
  "UPDATE",
  "POST",
  "UNPOST",
  "POST_TO_ISAP",
  "REVISION",
  "APPROVE",
  "REJECT",
  "DISCARD",
  "SUBMIT",
  "DELETE",
  "ADD_LINES",
  "REMOVE_LINES",
  "POST_LINES",
  "UNPOST_LINES",
  "EXPORT",
  "SPLIT",
  "OFFICIAL",
] as const

export type Intention = (typeof INTENTIONS)[number]

const AuthorisedActions: Record<Intention, Authority[]> = {
  UPDATE: ["AUTH_EDIT_BOOKING_ORDER"],
  POST: ["AUTH_EDIT_BOOKING_ORDER"],
  UNPOST: ["AUTH_EDIT_BOOKING_ORDER"],
  POST_TO_ISAP: ["AUTH_EDIT_BOOKING_ORDER"],
  REVISION: ["AUTH_EDIT_BOOKING_ORDER"],
  APPROVE: [],
  REJECT: [],
  DISCARD: [],
  SUBMIT: [],
  DELETE: ["AUTH_DELETE_BOOKING_ORDER"],
  ADD_LINES: ["AUTH_EDIT_BOOKING_ORDER"],
  REMOVE_LINES: ["AUTH_EDIT_BOOKING_ORDER"],
  POST_LINES: ["AUTH_EDIT_BOOKING_ORDER"],
  UNPOST_LINES: ["AUTH_EDIT_BOOKING_ORDER"],
  EXPORT: ["AUTH_EDIT_BOOKING_ORDER"],
  SPLIT: ["AUTH_SPLIT_LINE_ITEM"],
  OFFICIAL: ["AUTH_EDIT_BOOKING_ORDER"],
}

const BOLifecyle: Record<
  BookingOrderStatus,
  Record<BoViewType, Intention[]>
> = {
  POSTED: {
    CLIENT_AGENCY: [
      "POST_TO_ISAP",
      "EXPORT",
      "OFFICIAL",
      "UNPOST",
      "UNPOST_LINES",
    ],
    INTERCOMPANY: [
      "POST_TO_ISAP",
      "EXPORT",
      "OFFICIAL",
      "UNPOST",
      "UNPOST_LINES",
    ],
  },
  BOOKED: {
    CLIENT_AGENCY: [
      "POST",
      "UPDATE",
      "REVISION",
      "POST_LINES",
      "UNPOST_LINES",
      "DELETE",
      "ADD_LINES",
      "REMOVE_LINES",
      "EXPORT",
      "SPLIT",
      "OFFICIAL",
    ],
    INTERCOMPANY: [
      "POST",
      "REVISION",
      "POST_LINES",
      "UNPOST_LINES",
      "EXPORT",
      "OFFICIAL",
    ],
  },
  UNDER_REVISION: {
    CLIENT_AGENCY: ["UPDATE", "SUBMIT", "DISCARD"],
    INTERCOMPANY: ["EXPORT"],
  },
  UNDER_INTERCOMPANY_REVISION: {
    CLIENT_AGENCY: ["UPDATE", "EXPORT"],
    INTERCOMPANY: ["SUBMIT", "DISCARD"],
  },
  PENDING_REVISION: {
    CLIENT_AGENCY: ["UPDATE", "APPROVE", "REJECT"],
    INTERCOMPANY: ["EXPORT"],
  },
  PENDING_INTERCOMPANY_REVISION: {
    CLIENT_AGENCY: ["UPDATE", "EXPORT"],
    INTERCOMPANY: ["APPROVE", "REJECT"],
  },
  POSTED_FOR_BILLING: {
    CLIENT_AGENCY: [],
    INTERCOMPANY: [],
  },
  CANCELLED: {
    CLIENT_AGENCY: [],
    INTERCOMPANY: [],
  },
}

export function isUserValidBOApprover(
  bookingOrder: Partial<BookingOrder>,
  userMeta: UserMeta | undefined,
): boolean {
  if (!userMeta?.id) {
    return false
  }
  const amIInApproversList =
    bookingOrder?.revision?.approvers?.map((x) => x.id).includes(userMeta.id) ??
    false

  return amIInApproversList
}

export function getAvailableUserIntentions(
  bookingOrder: Partial<BookingOrder> | undefined,
  userMeta: UserMeta | undefined,
  intentions: Intention[],
): Intention[] {
  if (!bookingOrder) {
    return []
  }

  let _intentions: Intention[] = intentions

  // if not a valid approver, cut APPROVE & REJECT actions
  const isValidApprover = isUserValidBOApprover(bookingOrder, userMeta)
  if (!isValidApprover) {
    _intentions = _intentions?.filter(
      (int) => !["APPROVE", "REJECT"].includes(int),
    )
  }

  //if post to isap is disabled, cut POST_TO_ISAP action
  if (!bookingOrder.postingToIsapEnabled) {
    _intentions = _intentions?.filter((int) => !["POST_TO_ISAP"].includes(int))
  }

  // authorised
  _intentions = _intentions?.filter((intention) => {
    const authoritiesForThisIntention = AuthorisedActions[intention]
    return checkAuthority(userMeta, authoritiesForThisIntention)
  })

  return _intentions
}

export function getAvailableStatusIntentions(
  currentStatus?: BookingOrderStatus,
  viewType?: BoViewType,
): Intention[] {
  if (!currentStatus || !viewType) return []
  return BOLifecyle[currentStatus][viewType]
}
